'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Button from './Button';

interface NewsletterBannerMedia {
  id: number;
  url: string;
  alternativeText?: string | null;
  width?: number | null;
  height?: number | null;
  formats?: Record<string, unknown> | null;
}

interface NewsletterProps {
  onSubmit?: (email: string) => void | Promise<void>;
  /** Deshabilita input y CTA mientras se envía (p. ej. mutación newsletter) */
  isSubmitting?: boolean;
  width?: string;
  titulo?: string | null;
  descripcion?: string | null;
  objetoFondo?: NewsletterBannerMedia | null;
  objetoSuperior?: NewsletterBannerMedia | null;
  /** "sangrado" | "ajustado" | "alineacion izquierda" | "alineacion central" | "alineacion derecha" */
  objetoFondoAjuste?: string | null;
  /** "superior izquierda" | "superior centrado" | … | "derecha escalado" */
  objetoSuperiorPosicion?: string | null;
  /** "Izquierda" | "Centrado" | "Derecha" */
  alineacionTitulo?: string | null;
  /** "Superior" | "Centrado" | "Inferior" */
  posicionTitulo?: string | null;
  /** "Auto" | "Medium" | "Negrita" | "Cursiva" */
  fuenteTitulo?: string | null;
  boton?: boolean | null;
  textoBoton?: string | null;
  /** "cuadrante 1" … "cuadrante 8" — grilla 4 columnas × 2 filas */
  posicionBoton?: string | null;
  url?: string | null;
  colorFondoHex?: string | null;
  colorTextoTituloHex?: string | null;
  colorTextoDescripcionHex?: string | null;
}

const normalizeHex = (hex: string | null | undefined): string | undefined => {
  if (!hex || typeof hex !== 'string') return undefined;
  const trimmed = hex.trim().replace(/^#/, '');
  if (!trimmed) return undefined;
  return `#${trimmed}`;
};

const getFontClass = (fuente: string | null | undefined): string => {
  switch (fuente) {
    case 'Negrita': return 'font-tt-norms-extra-black';
    case 'Medium':  return 'font-tt-norms-medium';
    case 'Cursiva': return 'italic font-tt-norms-regular';
    default:        return 'font-tt-norms-extra-black';
  }
};

const getObjectFit = (ajuste: string | null | undefined): 'cover' | 'contain' =>
  ajuste === 'sangrado' ? 'cover' : 'contain';

const getObjectPosition = (ajuste: string | null | undefined): string => {
  switch (ajuste) {
    case 'alineacion izquierda': return 'left center';
    case 'alineacion derecha':   return 'right center';
    default:                     return 'center center';
  }
};

/**
 * Posiciona el bloque email+botón en una grilla de 4 columnas × 2 filas:
 *
 *  Q1 | Q2 | Q3 | Q4   ← fila superior
 *  Q5 | Q6 | Q7 | Q8   ← fila inferior
 */
const getNewsletterFormPosition = (posicionBoton: string): React.CSSProperties => {
  const match = posicionBoton.match(/\d+/);
  if (!match) return { bottom: '15%', left: '5%' };

  const q   = Math.max(1, Math.min(8, parseInt(match[0], 10)));
  const col = (q - 1) % 4 + 1; // 1 | 2 | 3 | 4
  const row = Math.ceil(q / 4); // 1 (superior) | 2 (inferior)

  const horizontal: React.CSSProperties =
    col === 1 ? { left: '5%' }   :
    col === 2 ? { left: '30%' }  :
    col === 3 ? { right: '30%' } :
                { right: '5%' };

  const vertical: React.CSSProperties =
    row === 1 ? { top: '15%' } : { bottom: '15%' };

  return { ...horizontal, ...vertical };
};

const Newsletter: React.FC<NewsletterProps> = ({
  onSubmit,
  isSubmitting = false,
  width = 'w-3/4',
  titulo,
  descripcion,
  objetoFondo,
  objetoSuperior,
  objetoFondoAjuste,
  alineacionTitulo,
  posicionTitulo,
  fuenteTitulo,
  boton,
  textoBoton,
  posicionBoton,
  url,
  colorFondoHex,
  colorTextoTituloHex,
  colorTextoDescripcionHex,
}) => {
  const [email, setEmail] = useState('');

  const bgColor    = normalizeHex(colorFondoHex);
  const tituloColor = normalizeHex(colorTextoTituloHex);
  const descColor   = normalizeHex(colorTextoDescripcionHex);

  const textAlign    = alineacionTitulo === 'Derecha' ? 'text-right' : 'text-left';
  const verticalAlign =
    posicionTitulo === 'Superior' ? 'items-start' :
    posicionTitulo === 'Inferior' ? 'items-end'   : 'items-center';

  const fontClass = getFontClass(fuenteTitulo);

  const useQuadrantForm =
    typeof posicionBoton === 'string' && /\d/.test(posicionBoton);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed || isSubmitting) return;
    try {
      await Promise.resolve(onSubmit?.(trimmed));
      setEmail('');
    } catch {
      /* conservar email para reintentar */
    }
  };

  const handleCtaClick = () => {
    if (url) {
      window.location.href = url;
      return;
    }
    void handleSubmit();
  };

  const placeholderStyles = `
    .nl-placeholder::placeholder {
      color: rgba(61, 28, 26, 0.54) !important;
    }
  `;

  const renderTituloDescripcion = () => (
    <>
      {titulo && (
        <p data-component-id="src/components/ui/Newsletter.tsx:162:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="162" data-component-file="Newsletter.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
          className={`${fontClass} text-[40px] leading-[47px] mb-4`}
          style={tituloColor ? { color: tituloColor } : { color: 'var(--color-global-1)' }}
        >
          {titulo}
        </p>
      )}
      {descripcion && (
        <p data-component-id="src/components/ui/Newsletter.tsx:170:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="170" data-component-file="Newsletter.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-2xl%20leading-7%22%7D"
          className="font-tt-norms-medium text-2xl leading-7"
          style={descColor ? { color: descColor } : { color: 'var(--color-global-1)' }}
        >
          {descripcion}
        </p>
      )}
    </>
  );

  const renderFormFields = () => (
    <>
      <input data-component-id="src/components/ui/Newsletter.tsx:182:6" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="182" data-component-file="Newsletter.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3Aemail%5D%22%2C%22className%22%3A%22w-full%20max-w-%5B280px%5D%20bg-global-11%20rounded-%5B10px%5D%20px-4%20py-4%20font-tt-norms-medium%20text-base%20leading-5%20text-global-2%20outline-none%20nl-placeholder%22%7D"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Tu email"
        disabled={isSubmitting}
        className="w-full max-w-[280px] bg-global-11 rounded-[10px] px-4 py-4 font-tt-norms-medium text-base leading-5 text-global-2 outline-none nl-placeholder"
      />
      {boton !== false && (
        <Button data-component-id="src/components/ui/Newsletter.tsx:191:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="191" data-component-file="Newsletter.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22min-w-%5B7.5rem%5D%20w-fit%20h-%5B38px%5D%20px-4%20text-%5B18px%5D%20shrink-0%20whitespace-nowrap%22%7D"
          variant="global-bg-6-text"
          type="button"
          onClick={handleCtaClick}
          disabled={isSubmitting}
          className="min-w-[7.5rem] w-fit h-[38px] px-4 text-[18px] shrink-0 whitespace-nowrap"
          style={{ color: '#FFFFFF' }}
        >
          {isSubmitting ? 'Enviando...' : textoBoton || 'Unirme'}
        </Button>
      )}
    </>
  );

  return (
    <div data-component-id="src/components/ui/Newsletter.tsx:206:4" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="206" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`rounded-[20px] relative overflow-hidden ${width} ${!bgColor ? 'bg-global-6' : ''}`}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      <style data-component-id="src/components/ui/Newsletter.tsx:210:6" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="210" data-component-file="Newsletter.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{placeholderStyles}</style>

      {/* Imagen de fondo (Objeto_fondo) */}
      {objetoFondo?.url && (
        <div data-component-id="src/components/ui/Newsletter.tsx:214:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="214" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20z-0%22%7D" className="absolute inset-0 z-0">
          <Image data-component-id="src/components/ui/Newsletter.tsx:215:10" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="215" data-component-file="Newsletter.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22pointer-events-none%22%7D"
            src={objetoFondo.url}
            alt={objetoFondo.alternativeText ?? 'Newsletter fondo'}
            fill
            className="pointer-events-none"
            style={{
              objectFit: getObjectFit(objetoFondoAjuste),
              objectPosition: getObjectPosition(objetoFondoAjuste),
            }}
          />
        </div>
      )}

      {/* Imagen superior / decorativa (Objeto_superior) */}
      {objetoSuperior?.url && (
        <div data-component-id="src/components/ui/Newsletter.tsx:230:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="230" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20right-0%20bottom-0%20w-1%2F3%20rounded-r-%5B20px%5D%20overflow-hidden%20z-0%22%7D" className="absolute top-0 right-0 bottom-0 w-1/3 rounded-r-[20px] overflow-hidden z-0">
          <Image data-component-id="src/components/ui/Newsletter.tsx:231:10" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="231" data-component-file="Newsletter.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22h-full%20w-auto%20scale-120%20translate-x-%5B75px%5D%22%7D"
            src={objetoSuperior.url}
            alt={objetoSuperior.alternativeText ?? 'Newsletter imagen'}
            width={1600}
            height={1200}
            className="h-full w-auto scale-120 translate-x-[75px]"
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        </div>
      )}

      {/*
        El grid siempre existe para mantener el alto natural del componente.
        En modo cuadrante, el formulario sale del flujo (absolute) y las columnas 3-4 son espacio vacío.
      */}
      <div data-component-id="src/components/ui/Newsletter.tsx:246:6" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="246" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`p-12 grid grid-cols-4 gap-8 ${verticalAlign} relative z-10`}>
        <div data-component-id="src/components/ui/Newsletter.tsx:247:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="247" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`col-span-2 flex flex-col ${textAlign}`}>
          {renderTituloDescripcion()}
        </div>

        {useQuadrantForm ? (
          <div data-component-id="src/components/ui/Newsletter.tsx:252:10" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="252" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22col-span-2%22%7D" className="col-span-2" />
        ) : (
          <>
            <div data-component-id="src/components/ui/Newsletter.tsx:255:12" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="255" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22col-span-1%20flex%20flex-col%20items-end%20gap-4%20-ml-16%22%7D" className="col-span-1 flex flex-col items-end gap-4 -ml-16">
              {renderFormFields()}
            </div>
            <div data-component-id="src/components/ui/Newsletter.tsx:258:12" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="258" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22col-span-1%22%7D" className="col-span-1" />
          </>
        )}
      </div>

      {/* Formulario absoluto cuando hay cuadrante (4 col × 2 filas) */}
      {useQuadrantForm && (
        <div data-component-id="src/components/ui/Newsletter.tsx:265:8" data-component-path="src/components/ui/Newsletter.tsx" data-component-line="265" data-component-file="Newsletter.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20flex%20flex-col%20items-end%20gap-4%20z-20%22%7D"
          className="absolute flex flex-col items-end gap-4 z-20"
          style={getNewsletterFormPosition(posicionBoton!)}
        >
          {renderFormFields()}
        </div>
      )}
    </div>
  );
};

export default Newsletter;

